// Dev mocks for tellent-assistant.
//
// Drop-in replacements for `TellentAssistantMastraStreamClient` and
// `TellentAssistantMastraClientService` that play scripted chunk sequences
// instead of hitting the backend. Lets you preview every assistant state
// inside the real app shell (real overlay, real styles, real icons).
//
// Gated by `DEV_ASSISTANT_MOCKS_ENABLED` (below). Picker UI selects the
// active fixture at runtime.

import { inject, Injectable, InjectionToken, type Provider } from '@angular/core';

import { Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';

import type { ChatMessage, ThreadSummary } from '@core/tellent-assistant-types';
import {
  MastraStreamAccumulator,
  toChatMessages,
  toMastraUIMessages,
  toMastraUserMessage,
} from '@core/tellent-assistant-util';

import { type DevAssistantFixture, DEV_ASSISTANT_FIXTURES } from './dev-mocks.fixtures';
import { TellentAssistantMastraStreamClient } from '../tellent-assistant-mastra-stream.client';
import { TellentAssistantMastraClientService } from '../tellent-assistant-mastra.client.service';

export { type DevAssistantFixture, DEV_ASSISTANT_FIXTURES } from './dev-mocks.fixtures';

/** Dev-only manual toggle. Flip to `true` locally to enable mocks + picker. Never commit `true`. */
export const DEV_ASSISTANT_MOCKS_ENABLED = false;

const DEV_ASSISTANT_FIXTURE = new InjectionToken<DevAssistantFixture>('DEV_ASSISTANT_FIXTURE');
const CHUNK_DELAY_MS = 20;

@Injectable()
class DevMockTellentAssistantMastraStreamClient {
  private readonly fixture = inject(DEV_ASSISTANT_FIXTURE);

  private readonly accumulator = new MastraStreamAccumulator();
  private readonly messagesSubject = new ReplaySubject<readonly ChatMessage[]>(1);
  private readonly runErrorSubject = new Subject<{ message: string }>();
  private readonly runFinalizedSubject = new Subject<void>();

  public readonly messages$: Observable<readonly ChatMessage[]> =
    this.messagesSubject.asObservable();
  public readonly runError$: Observable<{ message: string }> = this.runErrorSubject.asObservable();
  public readonly runFinalized$: Observable<void> = this.runFinalizedSubject.asObservable();

  private threadId: string | null = null;
  private cancelled = false;

  public setThreadId(threadId: string): void {
    this.cancelRun();
    this.threadId = threadId;
  }

  public markThreadAsNew(): void {
    /* no-op in dev mocks */
  }

  public setMessages(msgs: readonly ChatMessage[]): void {
    this.cancelRun();
    this.accumulator.setMessages(toMastraUIMessages(msgs));
    this.emitMessages();
  }

  public addUserMessage(
    text: string,
    metadata?: Record<string, unknown>,
  ): ReturnType<typeof toMastraUserMessage> {
    const msg = toMastraUserMessage(text, metadata);
    this.accumulator.setMessages([...this.accumulator.messages, msg]);
    this.emitMessages();
    return msg;
  }

  public async run(): Promise<void> {
    this.cancelled = false;
    if (!this.threadId) {
      this.runErrorSubject.next({ message: 'No thread selected' });
      this.runFinalizedSubject.next();
      return;
    }
    if (this.fixture.responseError) {
      this.runErrorSubject.next(this.fixture.responseError);
      this.runFinalizedSubject.next();
      return;
    }
    for (const chunk of this.fixture.responseChunks ?? []) {
      if (this.cancelled) break;
      this.accumulator.apply(chunk);
      this.emitMessages();
      await new Promise(resolve => setTimeout(resolve, CHUNK_DELAY_MS));
    }
    this.runFinalizedSubject.next();
  }

  public cancelRun(): void {
    this.cancelled = true;
  }

  private emitMessages(): void {
    this.messagesSubject.next(toChatMessages(this.accumulator.messages));
  }
}

@Injectable()
class DevMockTellentAssistantMastraClientService {
  private readonly fixture = inject(DEV_ASSISTANT_FIXTURE);

  public listThreads(): Observable<ThreadSummary[]> {
    return of([...(this.fixture.threads ?? [])]);
  }

  public loadMessages(threadId: string): Observable<unknown[]> {
    return of([...(this.fixture.threadMessages?.[threadId] ?? [])]);
  }

  public deleteThread(): Observable<void> {
    return of(void 0);
  }

  public setThreadPinned(threadId: string, pinned: boolean): Observable<ThreadSummary> {
    const thread = this.fixture.threads?.find(t => t.id === threadId);
    if (!thread) return throwError(() => new Error(`Unknown thread ${threadId}`));
    return of({ ...thread, pinned });
  }

  public patchThreadMetadata(threadId: string): Observable<ThreadSummary> {
    const thread = this.fixture.threads?.find(t => t.id === threadId);
    if (!thread) return throwError(() => new Error(`Unknown thread ${threadId}`));
    return of(thread);
  }
}

export function provideTellentAssistantDevMocks(fixtureId: string): Provider[] {
  if (!DEV_ASSISTANT_MOCKS_ENABLED) return [];
  const fixture = DEV_ASSISTANT_FIXTURES.find(f => f.id === fixtureId);
  if (!fixture) {
    throw new Error(
      `Unknown tellent-assistant dev fixture: ${fixtureId}. ` +
        `Available: ${DEV_ASSISTANT_FIXTURES.map(f => f.id).join(', ')}`,
    );
  }
  return [
    { provide: DEV_ASSISTANT_FIXTURE, useValue: fixture },
    {
      provide: TellentAssistantMastraStreamClient,
      useClass: DevMockTellentAssistantMastraStreamClient,
    },
    {
      provide: TellentAssistantMastraClientService,
      useClass: DevMockTellentAssistantMastraClientService,
    },
  ];
}
